import {useLocalization} from "@fluent/react";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import {
	Box,
	InputBase,
	MenuItem,
	Select,
	Stack,
	Typography,
	tablePaginationClasses,
} from "@mui/material";
import useId from "@mui/material/utils/useId";
import React from "react";

import IconButtonWithTooltip from "../IconButtonWithTooltip";

const defaultPageSizeOptions = [10, 25, 50];

function ServerPaginationControls(props: {
	disabledControls?: {
		first: boolean;
		next: boolean;
		previous: boolean;
	};
	pageSizeOptions?: number[];
	pageSize?: number;
	label?: React.ReactNode;
	onPageSizeChange?: (val: number) => void;
	onClick: (rel: "first" | "next" | "previous") => void;
}) {
	const {disabledControls, onClick} = props;

	const {l10n} = useLocalization();

	const labelBeginning = l10n.getString("server-pagination-controls-beginning");
	const labelPrevious = l10n.getString("server-pagination-controls-previous");
	const labelNext = l10n.getString("server-pagination-controls-next");

	const rowsPerPage = props.onPageSizeChange
		? props.pageSizeOptions ?? defaultPageSizeOptions
		: null;

	const labelId = useId();

	return (
		<Stack
			direction="row"
			sx={{fontSize: (theme) => theme.typography.pxToRem(14)}}
		>
			<Box sx={{flex: "1 1 100%"}} />

			{rowsPerPage && rowsPerPage.length > 0 && (
				<>
					<Typography
						variant="body2"
						sx={{flexShrink: 0, margin: "auto"}}
						id={labelId}
					>
						{props.label}
					</Typography>
					<Select
						variant="standard"
						labelId={labelId}
						value={props.pageSize}
						input={<InputBase />}
						classes={{
							select: tablePaginationClasses.select,
						}}
						onChange={({target}) => {
							props.onPageSizeChange?.(target.value as number);
						}}
						sx={{
							color: "inherit",
							fontSize: "inherit",
							flexShrink: 0,
							mr: 4,
							ml: 1,
							[`& .${tablePaginationClasses.select}`]: {
								paddingLeft: 1,
								paddingRight: 3,
								textAlign: "right",
								textAlignLast: "right",
							},
						}}
					>
						{rowsPerPage.map((r) => (
							<MenuItem key={r} value={r}>
								{r}
							</MenuItem>
						))}
					</Select>
				</>
			)}

			<IconButtonWithTooltip
				tooltipTitle={labelBeginning}
				disabled={disabledControls?.first}
				aria-label={labelBeginning}
				onClick={() => onClick("first")}
			>
				<FirstPageIcon />
			</IconButtonWithTooltip>
			<IconButtonWithTooltip
				tooltipTitle={labelPrevious}
				disabled={disabledControls?.previous}
				aria-label={labelPrevious}
				onClick={() => onClick("previous")}
			>
				<KeyboardArrowLeft />
			</IconButtonWithTooltip>
			<IconButtonWithTooltip
				tooltipTitle={labelNext}
				disabled={disabledControls?.next}
				aria-label={labelNext}
				onClick={() => onClick("next")}
			>
				<KeyboardArrowRight />
			</IconButtonWithTooltip>
		</Stack>
	);
}

export default ServerPaginationControls;
